/**
 * Investeringsplaner — modifierar timdata baserat på planerade åtgärder
 * (isolering, kompletterande värme, elbil) innan batterisimulering körs.
 *
 * Tre effektnivåer per åtgärdstyp för att slippa kräva att användaren
 * själv uppskattar kWh-besparingar. Försiktig/Neutral/Optimistisk speglar
 * verkliga spann från branschen.
 */

import type { HourPoint } from "./types";

export type InvestmentPlanType =
  | "insulation" // minskar uppvärmningsimport (okt-apr, proportionellt)
  | "supplementary_heating" // minskar elimport kvällstimmar 16-22
  | "ev"; // ökar nattimport 22-05

export type InvestmentEffectLevel = "conservative" | "neutral" | "optimistic";

export interface InvestmentPlan {
  id: string;
  type: InvestmentPlanType;
  enabled: boolean;
  investmentCostSek: number;
  effectLevel: InvestmentEffectLevel;
}

export interface InvestmentPlans {
  enabled: boolean;
  plans: InvestmentPlan[];
}

/**
 * Preset kWh-värden per år för varje åtgärdstyp och effektnivå.
 *
 * Isolering och kompletterande värme skalas dynamiskt baserat på husets
 * faktiska förbrukning — det är meningslöst att föreslå 1500 kWh isolerings-
 * besparing till någon med 5000 kWh/år eller 13000 kWh/år som standardvärde.
 *
 * Elbil är statisk eftersom den beror på körsträcka, inte hushållets förbrukning.
 *
 * Källor:
 * - Isolering: Energimyndigheten + branschdata. Andel av vinterförbrukning
 *   som kan elimineras genom isoleringsåtgärder.
 * - Kompletterande värme (kamin): Pelletskamin täcker typiskt en
 *   betydande del av kvällskonsumtionen vintertid.
 * - Elbil: 50-150 mil/vecka × ~0.2 kWh/km. Smart laddning antas.
 */

/**
 * Beräknar dynamiska kWh-spann baserat på användarens faktiska förbrukning.
 * Faller tillbaka till statiska defaultvärden om data saknas.
 *
 * Procentsatser är moderata bedömningar från branschen — en omfattande
 * isolering av en typisk villa kan minska vinterförbrukningen med 30-40%,
 * men det kräver stora investeringar. Mindre åtgärder ger 8-15%.
 */
export function getDynamicPreset(
  type: InvestmentPlanType,
  level: InvestmentEffectLevel,
  context?: { annualImportKWh?: number; winterImportKWh?: number; eveningImportKWh?: number },
): number {
  // Elbil är statisk
  if (type === "ev") {
    return INVESTMENT_PRESETS.ev.levels[level].kWhPerYear;
  }

  if (type === "insulation") {
    const winter = context?.winterImportKWh ?? 0;
    if (winter < 1000) {
      // För lite data — använd statiska defaults
      return INVESTMENT_PRESETS.insulation.levels[level].kWhPerYear;
    }
    // Andel av vinterimport (okt-apr) som åtgärden eliminerar
    const fractionByLevel: Record<InvestmentEffectLevel, number> = {
      conservative: 0.08,
      neutral: 0.2,
      optimistic: 0.35,
    };
    return Math.round((winter * fractionByLevel[level]) / 100) * 100;
  }

  if (type === "supplementary_heating") {
    const evening = context?.eveningImportKWh ?? 0;
    if (evening < 500) {
      return INVESTMENT_PRESETS.supplementary_heating.levels[level].kWhPerYear;
    }
    // Andel av kvällsimport (16-22) som ersätts av kompletterande värme
    const fractionByLevel: Record<InvestmentEffectLevel, number> = {
      conservative: 0.1,
      neutral: 0.25,
      optimistic: 0.45,
    };
    return Math.round((evening * fractionByLevel[level]) / 100) * 100;
  }

  return 0;
}

export const INVESTMENT_PRESETS: Record<
  InvestmentPlanType,
  {
    label: string;
    levels: Record<InvestmentEffectLevel, { kWhPerYear: number; description: string }>;
    defaultCostSek: number;
    helpText: string;
  }
> = {
  insulation: {
    label: "Isolering / fönsterbyte",
    levels: {
      conservative: {
        kWhPerYear: 500,
        description: "Mindre åtgärd (några fönster, tilläggsisolering vind)",
      },
      neutral: {
        kWhPerYear: 1500,
        description: "Medelstor åtgärd (flera fönster + isolering)",
      },
      optimistic: {
        kWhPerYear: 3000,
        description: "Stor åtgärd (samtliga fönster + omfattande isolering)",
      },
    },
    defaultCostSek: 50000,
    helpText:
      "Fördelas över uppvärmningssäsong (okt-apr) proportionellt mot import. Spannet är brett — verklig besparing kräver detaljerad energiberäkning av huset.",
  },
  supplementary_heating: {
    label: "Kompletterande värme (kamin etc)",
    levels: {
      conservative: {
        kWhPerYear: 500,
        description: "Sporadisk användning (helger, kalla kvällar)",
      },
      neutral: {
        kWhPerYear: 1500,
        description: "Regelbunden eldning under uppvärmningssäsong",
      },
      optimistic: {
        kWhPerYear: 3000,
        description: "Intensiv användning som primär värmekälla i delar av huset",
      },
    },
    defaultCostSek: 30000,
    helpText:
      "Antas ersätta el på kvällstimmar (16-22). Pellets/ved är inte gratis — räkna med ~80 öre/kWh värme vid beräkning av nettobesparing.",
  },
  ev: {
    label: "Elbil",
    levels: {
      conservative: {
        kWhPerYear: 2600,
        description: "~50 mil/vecka (kortare arbetspendling eller andrabil)",
      },
      neutral: {
        kWhPerYear: 5200,
        description: "~100 mil/vecka (vanlig arbetspendling)",
      },
      optimistic: {
        kWhPerYear: 7800,
        description: "~150 mil/vecka (lång pendling eller mycket körning)",
      },
    },
    defaultCostSek: 0, // elbilen är inte en investering i appens kontext
    helpText:
      "Antas laddas smart på nattimmar (22-05). Mer nattimport ger MER att arbitragera för batteriet, så elbil kan faktiskt förbättra batteriets ROI.",
  },
};

export const DEFAULT_INVESTMENT_PLANS: InvestmentPlans = {
  enabled: false,
  plans: [],
};

/**
 * Skapar en ny plan med defaults för en given typ.
 */
export function createDefaultPlan(type: InvestmentPlanType): InvestmentPlan {
  const preset = INVESTMENT_PRESETS[type];
  return {
    id: `${type}-${Date.now()}`,
    type,
    enabled: true,
    investmentCostSek: preset.defaultCostSek,
    effectLevel: "neutral",
  };
}

/**
 * Beräknar kontext för dynamiska presets baserat på timdata.
 * Räknar ut total årsimport, vinterimport (okt-apr) och kvällsimport (16-22).
 */
export function calculateImportContext(hours: HourPoint[]): {
  annualImportKWh: number;
  winterImportKWh: number;
  eveningImportKWh: number;
} {
  let annual = 0;
  let winter = 0;
  let evening = 0;
  for (const h of hours) {
    annual += h.importKWh;
    const date = new Date(h.from);
    const month = date.getMonth();
    const hour = date.getHours();
    if (month >= 9 || month <= 3) winter += h.importKWh;
    if (hour >= 16 && hour < 22) evening += h.importKWh;
  }
  return {
    annualImportKWh: annual,
    winterImportKWh: winter,
    eveningImportKWh: evening,
  };
}

/**
 * Returnerar årlig kWh-effekt för en plan (negativ = minskar import).
 * Använder dynamisk preset om context tillhandahålls.
 */
export function getPlanAnnualKWh(
  plan: InvestmentPlan,
  context?: { annualImportKWh?: number; winterImportKWh?: number; eveningImportKWh?: number },
): number {
  const annual = context
    ? getDynamicPreset(plan.type, plan.effectLevel, context)
    : INVESTMENT_PRESETS[plan.type].levels[plan.effectLevel].kWhPerYear;
  // EV ökar import (positivt), övriga minskar (negativt)
  return plan.type === "ev" ? annual : -annual;
}

/**
 * Modifierar timdata baserat på aktiva investeringsplaner.
 * Returnerar en NY array — muterar inte input.
 *
 * Strategi per typ:
 * - Insulation: proportionell reduktion av import under okt-apr
 * - Supplementary heating: proportionell reduktion av import under kvällar (16-22)
 * - EV: jämn fördelning av tillägg över nattimmar (22-05)
 */
export function applyInvestmentPlansToHours(
  hours: HourPoint[],
  plans: InvestmentPlans
): HourPoint[] {
  if (!plans.enabled || plans.plans.length === 0) {
    return hours;
  }

  const activePlans = plans.plans.filter((p) => p.enabled);
  if (activePlans.length === 0) return hours;

  // Räkna kontext en gång — används av alla planer för dynamiska presets
  const context = calculateImportContext(hours);

  // Skapa muterbar kopia
  const modified = hours.map((h) => ({ ...h }));

  for (const plan of activePlans) {
    applyPlanToHours(modified, plan, context);
  }

  return modified;
}

function applyPlanToHours(
  hours: HourPoint[],
  plan: InvestmentPlan,
  context: { annualImportKWh: number; winterImportKWh: number; eveningImportKWh: number },
): void {
  const annualKWh = getPlanAnnualKWh(plan, context);

  switch (plan.type) {
    case "insulation":
      reduceImportInRange(hours, annualKWh, (date) => {
        const month = date.getMonth(); // 0-11
        // Okt-apr = uppvärmningssäsong (okt=9, nov=10, dec=11, jan=0, feb=1, mar=2, apr=3)
        return month >= 9 || month <= 3;
      });
      break;

    case "supplementary_heating":
      reduceImportInRange(hours, annualKWh, (date) => {
        const hour = date.getHours();
        return hour >= 16 && hour < 22;
      });
      break;

    case "ev":
      addImportInRange(hours, annualKWh, (date) => {
        const hour = date.getHours();
        return hour >= 22 || hour < 5;
      });
      break;
  }
}

/**
 * Reducerar import över ett antal timmar proportionellt mot deras importmängd.
 * Om totalReduction överstiger totalImport i intervallet, sätts alla till 0.
 */
function reduceImportInRange(
  hours: HourPoint[],
  reductionKWh: number,
  filter: (date: Date) => boolean
): void {
  // reductionKWh är negativ (minskning), gör om till positivt belopp
  const totalReduction = Math.abs(reductionKWh);

  // Samla relevanta timmars index och total import
  const indices: number[] = [];
  let totalImport = 0;
  for (let i = 0; i < hours.length; i++) {
    const date = new Date(hours[i]!.from);
    if (filter(date)) {
      indices.push(i);
      totalImport += hours[i]!.importKWh;
    }
  }

  if (indices.length === 0 || totalImport === 0) return;

  // Skala faktor — om reduction > totalImport, cap till 100% reduktion
  const scaleFactor = Math.min(1, totalReduction / totalImport);

  for (const i of indices) {
    const h = hours[i]!;
    h.importKWh = h.importKWh * (1 - scaleFactor);
  }
}

/**
 * Lägger till import i ett intervall, jämnt fördelat över timmarna i intervallet.
 */
function addImportInRange(
  hours: HourPoint[],
  additionKWh: number,
  filter: (date: Date) => boolean
): void {
  const indices: number[] = [];
  for (let i = 0; i < hours.length; i++) {
    const date = new Date(hours[i]!.from);
    if (filter(date)) {
      indices.push(i);
    }
  }

  if (indices.length === 0) return;

  const perHour = additionKWh / indices.length;

  for (const i of indices) {
    hours[i]!.importKWh += perHour;
  }
}

/**
 * Beräknar besparing (kr/år) för en plan vid given importprisnivå.
 * Använder genomsnittspris för enkelhet — inte exakt men ger storleksordning.
 */
export function estimatePlanSavingsSek(
  plan: InvestmentPlan,
  avgImportPricePerKWh: number,
  context?: { annualImportKWh?: number; winterImportKWh?: number; eveningImportKWh?: number },
): number {
  const annualKWh = Math.abs(getPlanAnnualKWh(plan, context));
  if (plan.type === "ev") {
    // EV är en KOSTNAD i kalkylen — inte besparing
    return -annualKWh * avgImportPricePerKWh;
  }
  return annualKWh * avgImportPricePerKWh;
}

/**
 * Beräknar payback för en plan (år). Returnerar Infinity om besparing <= 0.
 */
export function calculatePlanPayback(
  plan: InvestmentPlan,
  avgImportPricePerKWh: number,
  context?: { annualImportKWh?: number; winterImportKWh?: number; eveningImportKWh?: number },
): number {
  const annualSavings = estimatePlanSavingsSek(plan, avgImportPricePerKWh, context);
  if (annualSavings <= 0) return Infinity;
  return plan.investmentCostSek / annualSavings;
}
